import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ExternalLink, Loader2, Eye, Send } from "lucide-react";
import PdfCanvasViewer from "@/components/PdfCanvasViewer";

const Shell = ({ label, children }) => (
  <div className="space-y-2">
    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="rounded-xl border border-border overflow-hidden bg-muted/30">{children}</div>
  </div>
);

const EmailFrame = ({ kind, testId }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    api.get("/brand/preview", { params: { kind } })
      .then(({ data: d }) => live && setData(d))
      .catch((err) => live && setError(err?.response?.data?.detail || "Could not render that preview"));
    return () => { live = false; };
  }, [kind]);

  if (error) return <div className="p-6 text-sm text-muted-foreground" data-testid={`${testId}-empty`}>{error}</div>;
  if (!data) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Rendering…
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Badge variant="secondary">To: {data.to}</Badge>
        <span className="font-medium" data-testid={`${testId}-subject`}>{data.subject || "(no subject)"}</span>
      </div>
      <iframe title={`${kind} preview`} srcDoc={data.html} data-testid={`${testId}-frame`}
              className="h-[460px] w-full rounded-xl border border-border bg-white" />
    </div>
  );
};

const SendTest = ({ kind, testId }) => {
  const { user } = useAuth();
  const [to, setTo] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/brand/preview/send", { kind, to });
      if (data.status === "sent") toast.success(`Test email on its way to ${data.to}`);
      else toast.info(data.detail || "Email provider not configured yet");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send that test");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${testId}-send-row`}>
      <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@studio.com"
             className="h-9 max-w-[260px]" data-testid={`${testId}-send-to`} />
      <Button size="sm" variant="outline" onClick={send} disabled={busy}
              data-testid={`${testId}-send-btn`}>
        {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              : <Send className="mr-1.5 h-3.5 w-3.5" />}
        Send me a test
      </Button>
    </div>
  );
};

/** See an invoice, an email and the public form exactly as a client would. */
export function ClientPreviewDialog({ open, onOpenChange }) {
  const [surfaces, setSurfaces] = useState(null);
  const [pdfData, setPdfData] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/brand/preview", { params: { kind: "surfaces" } })
      .then(({ data }) => setSurfaces(data))
      .catch(() => toast.error("Could not load the preview"));
  }, [open]);

  // The PDF route sends an attachment, so fetch the bytes and draw them ourselves.
  useEffect(() => {
    if (!open || !surfaces?.invoice_id) return;
    api.get(`/invoices/${surfaces.invoice_id}/pdf`, {
      params: { layout: surfaces.invoice_layout }, responseType: "arraybuffer",
    }).then(({ data }) => setPdfData(new Uint8Array(data)))
      .catch(() => toast.error("Could not render the invoice"));
  }, [open, surfaces?.invoice_id, surfaces?.invoice_layout]);

  const joinUrl = surfaces?.intake_token ? `/join/${surfaces.intake_token}` : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="client-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-4 w-4" /> Preview as a client
          </DialogTitle>
          <DialogDescription>
            {surfaces?.business_name || "Your brand"} — exactly what lands in front of the people
            you work with.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="invoice">
          <TabsList data-testid="client-preview-tabs">
            <TabsTrigger value="invoice" data-testid="preview-tab-invoice">Invoice PDF</TabsTrigger>
            <TabsTrigger value="reminder" data-testid="preview-tab-reminder">Reminder email</TabsTrigger>
            <TabsTrigger value="campaign" data-testid="preview-tab-campaign">Campaign email</TabsTrigger>
            <TabsTrigger value="join" data-testid="preview-tab-join">Sign-up form</TabsTrigger>
          </TabsList>

          <TabsContent value="invoice" className="pt-4">
            {pdfData ? (
              <Shell label={`Invoice ${surfaces.invoice_number || ""} · ${surfaces.invoice_layout} layout`}>
                <PdfCanvasViewer data={pdfData} testId="preview-invoice-frame"
                                filename={`${surfaces.invoice_number || "invoice"}.pdf`} />
              </Shell>
            ) : (
              <div className="p-6 text-sm text-muted-foreground" data-testid="preview-invoice-empty">
                {surfaces?.invoice_id ? "Rendering the invoice…"
                  : "Create an invoice in Finance and it'll show up here."}
              </div>
            )}
          </TabsContent>

          <TabsContent value="reminder" className="pt-4 space-y-3">
            <EmailFrame kind="reminder_email" testId="preview-reminder" />
            <SendTest kind="reminder_email" testId="preview-reminder" />
          </TabsContent>

          <TabsContent value="campaign" className="pt-4 space-y-3">
            <EmailFrame kind="campaign_email" testId="preview-campaign" />
            <SendTest kind="campaign_email" testId="preview-campaign" />
          </TabsContent>

          <TabsContent value="join" className="pt-4">
            {joinUrl ? (
              <div className="space-y-3" data-testid="preview-join">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    Public cast &amp; crew sign-up
                  </div>
                  <a href={joinUrl} target="_blank" rel="noreferrer" data-testid="preview-join-open">
                    <Button size="sm" variant="outline">
                      <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Open in a new tab
                    </Button>
                  </a>
                </div>
                <iframe title="Sign-up form preview" src={joinUrl} data-testid="preview-join-frame"
                        className="h-[520px] w-full rounded-xl border border-border bg-[#151C2B]" />
              </div>
            ) : (
              <div className="p-6 text-sm text-muted-foreground" data-testid="preview-join-empty">
                Turn the sign-up link on in Contacts → Sign-up link to preview it.
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
